import { useCallback } from "react";
import { convertFromMad, useCurrency } from "../../../lib/currency";

interface Priced {
  price: { sellTotal: number; currency: string } | null;
  fromPriceNightly?: number | null;
  fromPriceCurrency?: string | null;
}

/** Nights between two ISO dates, at least one. One when there are no dates. */
export function nightsBetween(checkin?: string, checkout?: string): number {
  if (!checkin || !checkout) return 1;
  return Math.max(1, Math.round((Date.parse(`${checkout}T00:00:00Z`) - Date.parse(`${checkin}T00:00:00Z`)) / 86_400_000));
}

/**
 * A stay's nightly price in the guest's currency, the number the price filter
 * compares, or null when there is nothing to compare.
 *
 * A live quote wins, as it does on the card. Without one, the stored "from"
 * price stands in; it is kept in MAD, so it is converted, and a row stored in
 * any other currency is left out rather than guessed at.
 */
export function useNightlyPrice(nights: number) {
  const { currency, rates } = useCurrency();
  return useCallback(
    (s: Priced): number | null => {
      if (s.price) {
        const nightly = s.price.sellTotal / nights;
        if (s.price.currency === currency) return nightly;
        if (s.price.currency === "MAD") return convertFromMad(nightly, currency, rates);
        return null;
      }
      if (!s.fromPriceNightly || !s.fromPriceCurrency) return null;
      if (s.fromPriceCurrency === currency) return s.fromPriceNightly;
      if (s.fromPriceCurrency === "MAD") return convertFromMad(s.fromPriceNightly, currency, rates);
      return null;
    },
    [currency, rates, nights],
  );
}
